import { computed, ref } from 'vue';
// Relative import keeps this loadable by the node test runner (no @ alias).
import { OTHER_REASON, SAVED_TOAST } from '../utils/questionFlags.js';
import {
  SORTY_REPORT_ENDPOINT,
  buildSortyReportPayload,
  canSubmitSortyReport,
} from '../utils/sortyReports.js';

function isSuccess(response) {
  return response?.status >= 200 && response?.status < 300;
}

function errorMessage(response) {
  if (response?.status === 429) {
    return "You've sent a lot of reports - please try again later.";
  }
  return 'Could not send your report. Please try again.';
}

/**
 * State and submit behind "Report sorty's behaviour". Always a fresh form:
 * there is no prefill or remove, and the student may report again.
 *
 * `context()` is read at submit time so the snapshot has the latest turns:
 * it returns { surface, links, turns }. `api` is ({ post }) and, like the
 * app's api helpers, resolves with error responses rather than throwing.
 */
export function useSortyReportForm({
  context,
  api,
  notify,
  onSubmitted = () => {},
  onClose = () => {},
}) {
  const selectedReason = ref(null);
  const comment = ref('');
  const submitting = ref(false);

  const detailsVisible = computed(() => selectedReason.value === OTHER_REASON);
  const submittable = computed(() =>
    canSubmitSortyReport({
      reason: selectedReason.value,
      comment: comment.value,
    })
  );

  function reset() {
    selectedReason.value = null;
    comment.value = '';
  }

  async function submit() {
    if (!submittable.value || submitting.value) return false;
    submitting.value = true;
    try {
      const { surface, links, turns } = context();
      const payload = buildSortyReportPayload({
        surface,
        reason: selectedReason.value,
        comment: comment.value,
        links,
        turns,
      });
      const response = await api.post(SORTY_REPORT_ENDPOINT, payload);
      if (!isSuccess(response)) {
        notify.error(errorMessage(response));
        return false;
      }
      onSubmitted({
        surface,
        reason: payload.reason,
        hasComment: Boolean(payload.comment),
        questionUuid: payload.question ?? payload.mock_exam_question ?? null,
      });
      notify.success(SAVED_TOAST);
      reset();
      onClose();
      return true;
    } catch {
      notify.error(errorMessage(null));
      return false;
    } finally {
      submitting.value = false;
    }
  }

  return {
    selectedReason,
    comment,
    submitting,
    detailsVisible,
    submittable,
    reset,
    submit,
  };
}
